/**
 * Live census + reconciliation panel (H2 v2 build item 1).
 *
 * "Live census-date preview updates as the schedule is edited, so the
 * coordinator sees the census consequence of every schedule choice."
 *
 * Everything shown here is computed by the server, which asks Postgres for the
 * ÷10 / ÷4 rule. Nothing on this panel is worked out in the browser — that is
 * what makes the preview trustworthy rather than merely reassuring.
 *
 * The hours reconciliation is shown as a WARNING while it is wrong, not as a
 * blocked button. The database is the thing that refuses to publish (M1); this
 * panel's job is to make sure the refusal is never a surprise.
 */
import { reportingPeriodLabel } from '../lib/sections.js';

function formatHours(n) {
  if (n === null || n === undefined) return '—';
  const value = Number(n);
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

/** How the scheduled total compares with the CCL approved hours. */
function reconciliation(scheduled, approved) {
  if (approved === null || approved === undefined || Number.isNaN(approved)) {
    return {
      tone: 'neutral',
      headline: 'Enter the State Board–approved hours to check this section reconciles.',
    };
  }
  const difference = Number((scheduled - approved).toFixed(2));

  if (difference === 0) {
    return {
      tone: 'ok',
      headline: `Reconciled — ${formatHours(scheduled)} scheduled hours match ${formatHours(approved)} approved hours.`,
    };
  }
  if (difference < 0) {
    return {
      tone: 'warn',
      headline: `Short by ${formatHours(Math.abs(difference))} hours — ${formatHours(scheduled)} scheduled against ${formatHours(approved)} approved.`,
      detail: 'Add meetings until the two match. The database will refuse to publish until they do.',
    };
  }
  return {
    tone: 'warn',
    headline: `Over by ${formatHours(difference)} hours — ${formatHours(scheduled)} scheduled against ${formatHours(approved)} approved.`,
    detail:
      'Never report more hours than the CCL approves. Trim the schedule, or check the approved-hours figure against the CCL.',
  };
}

export function CensusPreview({ preview, approvedHours, loading, error }) {
  if (error) {
    return (
      <section className="panel" aria-labelledby="census-heading">
        <h2 id="census-heading">Census &amp; reconciliation</h2>
        <p className="banner banner--error" role="alert">
          The preview could not be calculated: {error.message}
        </p>
      </section>
    );
  }

  if (!preview) {
    return (
      <section className="panel" aria-labelledby="census-heading">
        <h2 id="census-heading">Census &amp; reconciliation</h2>
        <p className="muted" aria-busy="true">
          Calculating…
        </p>
      </section>
    );
  }

  const recon = reconciliation(preview.scheduled_hours, approvedHours);
  const census = preview.census ?? {};

  return (
    <section className="panel" aria-labelledby="census-heading">
      <h2 id="census-heading">Census &amp; reconciliation</h2>
      <p className="muted">
        Worked out by the database from the schedule below, using the same rule that decides
        funding. {loading && <span aria-busy="true">Updating…</span>}
      </p>

      {/* aria-live: the coordinator's attention is on the schedule table, not
          here, so a changed census date has to announce itself. */}
      <div aria-live="polite">
        <p className={`callout callout--${recon.tone}`}>
          <strong>{recon.headline}</strong>
          {recon.detail && <span className="callout__detail">{recon.detail}</span>}
        </p>

        <dl className="detail-list">
          <div>
            <dt>Scheduled hours</dt>
            <dd>
              {formatHours(preview.scheduled_hours)}{' '}
              <span className="muted">
                across {preview.meeting_count} meeting{preview.meeting_count === 1 ? '' : 's'}
              </span>
            </dd>
          </div>
          <div>
            <dt>Runs</dt>
            <dd>
              {preview.start_date ? `${preview.start_date} to ${preview.end_date}` : '—'}
            </dd>
          </div>
          <div>
            <dt>Reporting period</dt>
            <dd>
              {reportingPeriodLabel(preview.reporting_period) ?? '—'}
              {preview.reporting_period && (
                <span className="muted"> · taken from the end date</span>
              )}
            </dd>
          </div>
          <div>
            <dt>Census date</dt>
            <dd>
              {census.censusDate ? (
                <strong>{census.censusDate}</strong>
              ) : census.window ? (
                <strong>
                  Window: {census.window.start} to {census.window.end}
                </strong>
              ) : (
                <span className="muted">Not applicable</span>
              )}
            </dd>
          </div>
        </dl>

        <p className={census.needsManualDate ? 'callout callout--warn' : 'muted'}>
          {census.explanation}
        </p>
      </div>
    </section>
  );
}

export default CensusPreview;
